import {createStyles, TextInput} from '@mantine/core';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import ClickOutWrapper from 'ui-library/ClickOutWrapper';
import {callOnEnter} from 'util/Keyboard';
import {InputChangeEvent, TextAreaChangeEvent} from 'util/Types';

const useStyles = createStyles((theme) => ({
  disableResize: {
    resize: 'none',
  },
  displayText: {
    width: '100%',
    overflowWrap: 'break-word',
  },
  clickableText: {
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
      cursor: 'pointer',
    },
  },
  input: {
    height: '28px',
  },
  valueFont: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.other.fontWeights.semibold,
  },
  placeholderFont: {
    fontSize: theme.fontSizes.lg,
    fontWeight: theme.other.fontWeights.normal,
  },
}));

export interface ClickToEditTextFieldProps {
  value: string;
  sanitize?: (val: string) => string;
  onSaveValue: (val: string) => void;
  placeholder?: string;
  spellCheck?: boolean;
  editable?: boolean;
  isArea?: boolean;
  disableResize?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Display a value with a click-to-edit mode.
 *
 * The field is editable after clicking into it. Once the user clicks away, the value is saved.
 *
 * @param value Value to display.
 * @param sanitize Callback to sanitize input. Run on every change the input.
 * @param onSaveValue Callback to save an updated value.
 * @param placeholder Placeholder to display if the value is falsy.
 * @param spellCheck Value for the spellcheck attribute on the text input element.
 * @param editable Whether the field can enter editing mode.
 * @param isArea Determines if a textarea or a text input is used for editing mode.
 * @param disableResize Disable the ability to resize if the component is a textarea.
 * @param dataTestAttrs Data test attributes to apply to the component.
 * @constructor
 */
export default function ClickToEditTextField({
  value,
  sanitize,
  onSaveValue,
  placeholder,
  spellCheck,
  editable = true,
  isArea = false,
  disableResize = true,
  dataTestAttrs,
}: ClickToEditTextFieldProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState<string | undefined>();
  const saveValue = (): void => {
    setEditing(false);
    if (tempValue !== undefined) {
      onSaveValue(tempValue);
    }
    setTempValue(undefined);
  };

  const sharedProps = {
    autoFocus: true,
    onChange: (e: InputChangeEvent | TextAreaChangeEvent) => {
      if (sanitize != null) {
        const sanitizedInput = sanitize(e.target.value);
        setTempValue(sanitizedInput);
      } else {
        setTempValue(e.target.value);
      }
    },
    onKeyPress: callOnEnter(saveValue),
    placeholder,
    spellCheck,
    value: tempValue,
  };

  const dataTestAttributes = getTestProps(dataTestAttrs);

  if (!editing) {
    const textStyles = [classes.displayText];
    if (editable) {
      textStyles.push(classes.clickableText);
      textStyles.push(value === '' ? classes.placeholderFont : classes.valueFont);
    } else {
      textStyles.push(classes.valueFont);
    }
    return (
      <div
        className={cx(...textStyles)}
        onClick={() => {
          if (!editable) {
            return;
          }
          setTempValue(value);
          setEditing(true);
        }}
        {...getTestProps(dataTestAttrs)}
      >
        {value || (editable ? placeholder : '')}
      </div>
    );
  } else {
    return (
      <ClickOutWrapper onClickOut={saveValue}>
        {isArea ? (
          <textarea
            {...sharedProps}
            {...dataTestAttributes}
            className={cx(classes.valueFont, disableResize && classes.disableResize)}
          />
        ) : (
          <TextInput
            {...sharedProps}
            wrapperProps={dataTestAttributes}
            classNames={{
              input: cx(classes.input, classes.valueFont),
            }}
          />
        )}
      </ClickOutWrapper>
    );
  }
}
